import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Shield, 
  CheckCircle2, 
  Loader2, 
  CreditCard,
  Building2,
  Mail,
  Phone,
  FileText,
  ArrowLeft,
  Sparkles,
  Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = 'AfXEoSHJmonM9Un3hJbGUrqh-KTLxb_vGL1n0-XEXNCojjg5y9fbYFriK_MJsR4S_T8W74onQygIAhcP';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const paypalRef = useRef(null);
  const [step, setStep] = useState(1); // 1: form, 2: payment, 3: success
  const [isLoading, setIsLoading] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [saleData, setSaleData] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    project_name: '',
    contact_email: '',
    contact_phone: '',
    amount: ''
  });

  // Load PayPal SDK
  useEffect(() => {
    if (step === 2 && !paypalLoaded) {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&components=buttons&currency=USD`;
      script.async = true;
      script.onload = () => setPaypalLoaded(true);
      document.body.appendChild(script);
      
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [step, paypalLoaded]);

  // Initialize PayPal Buttons
  useEffect(() => {
    if (paypalLoaded && step === 2 && saleData && paypalRef.current) {
      paypalRef.current.innerHTML = '';
      
      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'pay'
        },
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              description: `${saleData.project_name} - ${saleData.company_name}`,
              amount: {
                value: saleData.amount.toFixed(2)
              }
            }]
          });
        },
        onApprove: async (data, actions) => {
          setIsLoading(true);
          try {
            const details = await actions.order.capture();
            
            // Complete sale in backend
            const response = await axios.put(
              `${API}/sales/${saleData.id}/complete?paypal_order_id=${data.orderID}&paypal_transaction_id=${details.id}`
            );
            
            setSaleData(response.data.sale);
            setStep(3);
            toast.success('¡Pago completado exitosamente!');
          } catch (error) {
            console.error('Payment error:', error);
            toast.error('Error al procesar el pago');
          } finally {
            setIsLoading(false);
          }
        },
        onError: (err) => {
          console.error('PayPal error:', err);
          toast.error('Error en PayPal. Intente nuevamente.');
        }
      }).render(paypalRef.current);
    }
  }, [paypalLoaded, step, saleData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/sales`, {
        ...formData,
        amount: parseFloat(formData.amount),
        currency: 'USD'
      });
      
      setSaleData(response.data);
      setStep(2);
      toast.success('Datos registrados. Proceda al pago.');
    } catch (error) {
      console.error('Error creating sale:', error);
      toast.error('Error al registrar los datos');
    } finally {
      setIsLoading(false);
    }
  };

  const currentDate = new Date().toLocaleDateString('es-EC', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2F3D8E] via-[#1a2456] to-[#2F3D8E]">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver al inicio</span>
          </button>
          <div className="flex items-center gap-2 text-white">
            <Lock className="w-4 h-4 text-[#4C8C3B]" />
            <span className="text-sm">Pago Seguro</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center gap-4">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${
                  step >= s ? 'text-white' : 'text-white/40'
                }`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step > s ? 'bg-[#4C8C3B]' : step === s ? 'bg-[#5DA5D8]' : 'bg-white/20'
                  }`}>
                    {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
                  </div>
                  <span className="hidden sm:block text-sm font-medium">
                    {s === 1 ? 'Datos' : s === 2 ? 'Pago' : 'Confirmación'}
                  </span>
                </div>
                {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-[#4C8C3B]' : 'bg-white/20'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Form */}
        {step === 1 && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Sales Message */}
            <div className="bg-gradient-to-r from-[#4C8C3B] to-[#3d7030] p-6 text-white text-center">
              <Sparkles className="w-8 h-8 mx-auto mb-3" />
              <h2 className="text-2xl font-bold mb-2">
                ¡Estás a un paso de transformar tu negocio!
              </h2>
              <p className="opacity-90">
                Software 100% personalizado que automatiza tus procesos y multiplica tu productividad.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-[#2F3D8E]">Información del Proyecto</h3>
                <p className="text-gray-500 mt-2">Fecha: {currentDate}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-[#2F3D8E]">
                    <Building2 className="w-4 h-4" />
                    Nombre de la Empresa
                  </Label>
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Ej: Empresa ABC S.A."
                    required
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-[#2F3D8E]">
                    <FileText className="w-4 h-4" />
                    Nombre del Proyecto
                  </Label>
                  <Input
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleChange}
                    placeholder="Ej: Sistema de Inventarios"
                    required
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-[#2F3D8E]">
                    <Mail className="w-4 h-4" />
                    Correo de Contacto
                  </Label>
                  <Input
                    type="email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="correo@empresa.com"
                    required
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-[#2F3D8E]">
                    <Phone className="w-4 h-4" />
                    Teléfono de Contacto
                  </Label>
                  <Input
                    type="tel"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="+593 999 999 999"
                    required
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label className="flex items-center gap-2 text-[#2F3D8E]">
                    <CreditCard className="w-4 h-4" />
                    Monto a Pagar (USD)
                  </Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl font-bold">$</span>
                    <Input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      placeholder="0.00"
                      min="1"
                      step="0.01"
                      required
                      className="h-14 rounded-xl pl-10 text-2xl font-bold text-[#2F3D8E]"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 bg-[#2F3D8E] hover:bg-[#1a2456] text-white text-lg font-semibold rounded-xl shadow-lg"
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Procesando...</>
                ) : (
                  <><CreditCard className="mr-2 h-5 w-5" /> Continuar al Pago</>
                )}
              </Button>

              <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span>Pago Seguro</span>
                </div>
                <div className="flex items-center gap-1">
                  <Lock className="w-4 h-4" />
                  <span>Datos Encriptados</span>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && saleData && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#5DA5D8] to-[#2F3D8E] p-6 text-white text-center">
              <CreditCard className="w-8 h-8 mx-auto mb-3" />
              <h2 className="text-2xl font-bold mb-2">Completa tu Pago</h2>
              <p className="opacity-90">Tu inversión en productividad comienza ahora</p>
            </div>

            <div className="p-8">
              {/* Order Summary */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                <h3 className="text-lg font-bold text-[#2F3D8E] mb-4">Resumen de la Orden</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Voucher:</span>
                    <span className="font-mono font-bold text-[#4C8C3B]">{saleData.voucher_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Empresa:</span>
                    <span className="font-medium">{saleData.company_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Proyecto:</span>
                    <span className="font-medium">{saleData.project_name}</span>
                  </div>
                  <div className="border-t pt-3 mt-3 flex justify-between items-center">
                    <span className="text-lg font-bold text-[#2F3D8E]">Total a Pagar:</span>
                    <span className="text-3xl font-bold text-[#4C8C3B]">${saleData.amount.toFixed(2)} USD</span>
                  </div>
                </div>
              </div>

              {/* PayPal Button */}
              <div className="mb-6">
                {!paypalLoaded ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-[#2F3D8E]" />
                    <span className="ml-3 text-gray-600">Cargando PayPal...</span>
                  </div>
                ) : (
                  <div ref={paypalRef} />
                )}
              </div>

              {isLoading && (
                <div className="text-center py-4">
                  <Loader2 className="w-8 h-8 animate-spin text-[#2F3D8E] mx-auto" />
                  <p className="text-gray-600 mt-2">Procesando pago...</p>
                </div>
              )}

              <p className="text-center text-sm text-gray-400 mt-6">
                Al completar el pago, recibirás un comprobante digital en tu correo electrónico.
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && saleData && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#4C8C3B] to-[#3d7030] p-8 text-white text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12" />
              </div>
              <h2 className="text-3xl font-bold mb-2">¡Pago Exitoso!</h2>
              <p className="opacity-90 text-lg">Gracias por confiar en Analytica</p>
            </div>

            <div className="p-8 text-center">
              {/* Digital Voucher */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-8 border-2 border-dashed border-gray-200">
                <p className="text-sm text-gray-500 mb-2">VOUCHER DIGITAL</p>
                <p className="text-4xl font-bold text-[#2F3D8E] tracking-wider mb-4">
                  {saleData.voucher_number}
                </p>
                <div className="w-16 h-1 bg-[#4C8C3B] mx-auto mb-4" />
                <div className="grid grid-cols-2 gap-4 text-left max-w-md mx-auto">
                  <div>
                    <p className="text-xs text-gray-400">EMPRESA</p>
                    <p className="font-semibold text-[#2F3D8E]">{saleData.company_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">PROYECTO</p>
                    <p className="font-semibold text-[#2F3D8E]">{saleData.project_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">MONTO</p>
                    <p className="font-bold text-[#4C8C3B] text-xl">${saleData.amount.toFixed(2)} USD</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">ESTADO</p>
                    <p className="font-semibold text-[#4C8C3B]">✓ PAGADO</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#5DA5D8]/10 rounded-xl p-6 mb-8">
                <Sparkles className="w-8 h-8 text-[#5DA5D8] mx-auto mb-3" />
                <h3 className="text-xl font-bold text-[#2F3D8E] mb-2">
                  ¡Tu proyecto está en marcha!
                </h3>
                <p className="text-gray-600">
                  Nuestro equipo se pondrá en contacto contigo en las próximas 24 horas 
                  para iniciar el desarrollo de tu software personalizado.
                </p>
              </div>

              <p className="text-sm text-gray-400 mb-6">
                Hemos enviado una copia del comprobante a <strong>{saleData.contact_email}</strong>
              </p>

              <Button
                onClick={() => navigate('/')}
                className="bg-[#2F3D8E] hover:bg-[#1a2456] text-white px-8 py-3 rounded-xl"
              >
                Volver al Inicio
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
